<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <a-alert type="info" show-icon style="margin-bottom: 24px" message="连续签到规则说明">
        <template slot="description">
          <div>用户每日可签到一次，连续签到按第1天、第2天…依次领取对应奖励，可自由配置连续天数与金额。</div>
          <div style="margin-top: 4px">中断签到（未连续）将从第1天重新开始；完成最后一档后下一轮仍从第1天开始。</div>
          <div style="margin-top: 4px">App 端以日历展示当月签到记录及连续可获金额；奖励实时入账用户钱包，流水标题为 Daily Check-in。</div>
        </template>
      </a-alert>

      <a-spin :spinning="loading">
        <a-form-model :model="form" :label-col="{ span: 4 }" :wrapper-col="{ span: 16 }">
          <a-form-model-item label="启用签到">
            <a-switch
              :checked="form.checkinEnabled === 1"
              checked-children="是"
              un-checked-children="否"
              @change="onEnabledChange"
            />
          </a-form-model-item>

          <a-form-model-item label="连续奖励" required>
            <a-table
              :columns="columns"
              :data-source="form.rewards"
              :pagination="false"
              row-key="dayNo"
              size="middle"
              bordered
              style="max-width: 640px"
            >
              <span slot="dayNo" slot-scope="text">连续第 {{ text }} 天</span>
              <span slot="rewardAmount" slot-scope="text, record">
                <a-input-number
                  v-model="record.rewardAmount"
                  :min="0"
                  :step="0.01"
                  :precision="2"
                  style="width: 160px"
                  :disabled="form.checkinEnabled !== 1"
                />
              </span>
              <span slot="action" slot-scope="text, record, index">
                <a-button
                  type="link"
                  size="small"
                  :disabled="form.rewards.length <= 1"
                  @click="removeReward(index)"
                >删除</a-button>
              </span>
            </a-table>
            <div style="margin-top: 12px">
              <a-button
                type="dashed"
                icon="plus"
                :disabled="form.rewards.length >= maxRewardDays"
                @click="addReward"
              >添加一天</a-button>
              <span class="form-tip" style="margin-left: 12px">最多 {{ maxRewardDays }} 天，须从第1天起连续配置</span>
            </div>
            <div class="form-tip">启用签到时，每日奖励须大于 0</div>
          </a-form-model-item>

          <a-form-model-item :wrapper-col="{ span: 16, offset: 4 }">
            <a-button type="primary" :loading="submitLoading" @click="submitForm">保存配置</a-button>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { getCheckinConfig, saveCheckinConfig } from '@/api/giftcard/checkinConfig'

const DEFAULT_REWARDS = [
  { dayNo: 1, rewardAmount: 1 },
  { dayNo: 2, rewardAmount: 1 },
  { dayNo: 3, rewardAmount: 2 },
  { dayNo: 4, rewardAmount: 2 },
  { dayNo: 5, rewardAmount: 3 },
  { dayNo: 6, rewardAmount: 3 },
  { dayNo: 7, rewardAmount: 5 }
]

export default {
  name: 'GiftcardCheckinConfig',
  data() {
    return {
      loading: false,
      submitLoading: false,
      maxRewardDays: 31,
      form: {
        checkinEnabled: 1,
        rewards: DEFAULT_REWARDS.map(r => ({ ...r }))
      },
      columns: [
        { title: '连续天', dataIndex: 'dayNo', scopedSlots: { customRender: 'dayNo' }, width: 120 },
        { title: '奖励金额', dataIndex: 'rewardAmount', scopedSlots: { customRender: 'rewardAmount' } },
        { title: '操作', key: 'action', scopedSlots: { customRender: 'action' }, width: 80 }
      ]
    }
  },
  created() {
    this.loadConfig()
  },
  methods: {
    renumberRewards() {
      this.form.rewards.forEach((item, index) => {
        item.dayNo = index + 1
      })
    },
    addReward() {
      if (this.form.rewards.length >= this.maxRewardDays) {
        return
      }
      const last = this.form.rewards[this.form.rewards.length - 1]
      this.form.rewards.push({
        dayNo: this.form.rewards.length + 1,
        rewardAmount: last && last.rewardAmount != null ? last.rewardAmount : 1
      })
    },
    removeReward(index) {
      if (this.form.rewards.length <= 1) {
        return
      }
      this.form.rewards.splice(index, 1)
      this.renumberRewards()
    },
    loadConfig() {
      this.loading = true
      getCheckinConfig().then(res => {
        const data = res.data || {}
        const rewards = (data.rewards && data.rewards.length)
          ? data.rewards.map((r, index) => ({
            dayNo: r.dayNo != null ? r.dayNo : index + 1,
            rewardAmount: r.rewardAmount != null ? Number(r.rewardAmount) : 0
          }))
          : DEFAULT_REWARDS.map(r => ({ ...r }))
        rewards.sort((a, b) => a.dayNo - b.dayNo)
        rewards.forEach((item, index) => { item.dayNo = index + 1 })
        this.form = {
          checkinEnabled: data.checkinEnabled == null ? 0 : data.checkinEnabled,
          rewards
        }
      }).finally(() => { this.loading = false })
    },
    onEnabledChange(checked) {
      this.form.checkinEnabled = checked ? 1 : 0
    },
    submitForm() {
      this.renumberRewards()
      if (this.form.checkinEnabled === 1) {
        for (const item of this.form.rewards) {
          if (item.rewardAmount == null || item.rewardAmount <= 0) {
            this.$message.warning(`请填写连续第 ${item.dayNo} 天有效的奖励金额`)
            return
          }
        }
      }
      const payload = {
        checkinEnabled: this.form.checkinEnabled,
        rewards: this.form.rewards.map(r => ({
          dayNo: r.dayNo,
          rewardAmount: r.rewardAmount
        }))
      }
      this.submitLoading = true
      saveCheckinConfig(payload).then(() => {
        this.$message.success('保存成功')
        this.loadConfig()
      }).finally(() => { this.submitLoading = false })
    }
  }
}
</script>

<style scoped>
.form-tip { color: rgba(0, 0, 0, 0.45); font-size: 12px; margin-top: 8px; }
</style>
